import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Props {
  variant?: "default" | "outline" | "secondary" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  /** If true, hard-reload the page after refresh so all hooks re-fetch. Default true. */
  reload?: boolean;
  label?: string;
}

/**
 * One-click session refresh.
 *
 * Calls supabase.auth.refreshSession() to mint a new JWT (picking up tier,
 * role, and entitlement changes that happened server-side) without forcing
 * the user to sign out. Optionally hard-reloads so every hook re-reads
 * fresh server state.
 */
export const RefreshSessionButton = ({
  variant = "outline",
  size = "sm",
  className,
  reload = true,
  label = "Refresh session",
}: Props) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    setBusy(true);
    try {
      // Clear any cached founder flags so they re-resolve on next mount.
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && k.startsWith("wavlynq.isFounder.")) localStorage.removeItem(k);
        }
      } catch {
        /* ignore */
      }

      const { error } = await supabase.auth.refreshSession();
      if (error) throw error;

      toast({
        title: "Session refreshed",
        description: "Your tier and access have been updated.",
      });

      if (reload) {
        // Defer so the toast renders before reload
        setTimeout(() => window.location.reload(), 250);
      }
    } catch (e) {
      toast({
        title: "Refresh failed",
        description: e instanceof Error ? e.message : "Try signing out and back in.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={className}
      onClick={handleClick}
      disabled={busy}
    >
      <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""} ${label ? "mr-2" : ""}`} />
      {label}
    </Button>
  );
};

export default RefreshSessionButton;
